/**
 * Unified Activity Timeline
 * Shows CRM activities + Email engagements in one timeline
 */

import React, { useState, useEffect } from 'react';
import {
  Mail,
  Eye,
  MessageCircle,
  Phone,
  Calendar,
  FileText,
  CheckCircle,
  TrendingUp,
  MousePointerClick,
  Send
} from 'lucide-react';
import activitiesService, { Activity } from '../../services/crm/activitiesService';
import crmEmailBridgeService, { EmailEngagement } from '../../services/crm/crmEmailBridgeService';
import { formatDistanceToNow } from 'date-fns';
import { formatActivityContent } from '../../utils/activityFormatter';

interface UnifiedActivityTimelineProps {
  contactId: string;
  organizationId: string;
  entityType?: 'contact' | 'company' | 'deal';
  entityId?: string;
  limit?: number;
}

type UnifiedActivity = {
  id: string;
  type: 'note' | 'task' | 'call' | 'email' | 'meeting' | 'email_sent' | 'email_opened' | 'email_clicked' | 'email_replied';
  timestamp: string;
  description: string;
  metadata?: any;
  source: 'crm' | 'email';
};

const UnifiedActivityTimeline: React.FC<UnifiedActivityTimelineProps> = ({
  contactId,
  organizationId,
  entityType = 'contact',
  entityId,
  limit = 50
}) => {
  const [activities, setActivities] = useState<UnifiedActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'crm' | 'email'>('all');

  useEffect(() => {
    loadActivities();
  }, [contactId, organizationId, entityId]);

  const loadActivities = async () => {
    try {
      setIsLoading(true);

      // Load CRM activities and email engagements in parallel
      const [crmActivities, emailEngagements] = await Promise.all([
        activitiesService.list({
          organization_id: organizationId,
          entity_type: entityType,
          entity_id: entityId || contactId,
          limit
        }),
        crmEmailBridgeService.getContactEngagements(contactId, organizationId, limit)
      ]);

      // Convert CRM activities to unified format with human-readable descriptions
      const unifiedCrmActivities: UnifiedActivity[] = (crmActivities.data || []).map((activity: Activity) => ({
        id: activity.id,
        type: activity.type as any,
        timestamp: activity.created_at,
        description: formatActivityContent(activity as any),
        metadata: {},
        source: 'crm' as const
      }));

      // Convert email engagements to unified format
      const unifiedEmailActivities: UnifiedActivity[] = (emailEngagements || []).map((engagement: EmailEngagement) => ({
        id: engagement.id,
        type: `email_${engagement.engagement_type}` as any,
        timestamp: engagement.engaged_at,
        description: engagement.snippet || engagement.subject || '',
        metadata: {
          ...engagement.metadata,
          subject: engagement.subject,
          campaign_name: engagement.campaign_name,
          campaign_id: engagement.campaign_id
        },
        source: 'email' as const
      }));

      // Merge and sort by timestamp (newest first)
      const merged = [...unifiedCrmActivities, ...unifiedEmailActivities].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      setActivities(merged);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getActivityIcon = (type: UnifiedActivity['type']) => {
    switch (type) {
      case 'email':
      case 'email_sent':
        return <Send className="w-4 h-4 text-blue-500" />;
      case 'email_opened':
        return <Eye className="w-4 h-4 text-purple-500" />;
      case 'email_clicked':
        return <MousePointerClick className="w-4 h-4 text-indigo-500" />;
      case 'email_replied':
        return <MessageCircle className="w-4 h-4 text-emerald-500" />;
      case 'call':
        return <Phone className="w-4 h-4 text-green-500" />;
      case 'meeting':
        return <Calendar className="w-4 h-4 text-orange-500" />;
      case 'task':
        return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'note':
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActivityColor = (type: UnifiedActivity['type']) => {
    switch (type) {
      case 'email_sent':
        return 'bg-blue-50 ring-blue-200';
      case 'email_opened':
        return 'bg-purple-50 ring-purple-200';
      case 'email_clicked':
        return 'bg-indigo-50 ring-indigo-200';
      case 'email_replied':
        return 'bg-emerald-50 ring-emerald-200';
      case 'call':
        return 'bg-green-50 ring-green-200';
      case 'meeting':
        return 'bg-orange-50 ring-orange-200';
      case 'task':
        return 'bg-blue-50 ring-blue-200';
      case 'email':
        return 'bg-blue-50 ring-blue-200';
      case 'note':
      default:
        return 'bg-gray-50 ring-gray-200';
    }
  };

  const getActivityLabel = (type: UnifiedActivity['type']) => {
    switch (type) {
      case 'email_sent':
        return 'Email Sent';
      case 'email_opened':
        return 'Email Opened';
      case 'email_clicked':
        return 'Link Clicked';
      case 'email_replied':
        return 'Email Reply';
      case 'call':
        return 'Call';
      case 'meeting':
        return 'Meeting';
      case 'task':
        return 'Task';
      case 'email':
        return 'Email';
      case 'note':
      default:
        return 'Note';
    }
  };

  const filteredActivities = activities.filter(activity => {
    if (filter === 'all') return true;
    if (filter === 'crm') return activity.source === 'crm';
    if (filter === 'email') return activity.source === 'email';
    return true;
  });

  if (isLoading) {
    return (
      <div className="rounded-2xl bg-white p-6 ring-1 ring-gray-200">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white p-6 ring-1 ring-gray-200">
      {/* Header with Filters */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Activity Timeline</h3>
        
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({activities.length})
          </button>
          <button
            onClick={() => setFilter('crm')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              filter === 'crm'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            CRM ({activities.filter(a => a.source === 'crm').length})
          </button>
          <button
            onClick={() => setFilter('email')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              filter === 'email'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Email ({activities.filter(a => a.source === 'email').length})
          </button>
        </div>
      </div>

      {/* Timeline */}
      {filteredActivities.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No activities yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredActivities.map((activity, index) => (
            <div key={activity.id} className="flex gap-4">
              {/* Timeline Line */}
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ring-2 ${getActivityColor(activity.type)}`}>
                  {getActivityIcon(activity.type)}
                </div>
                {index < filteredActivities.length - 1 && (
                  <div className="w-0.5 h-full bg-gray-200 mt-2"></div>
                )}
              </div>

              {/* Activity Content */}
              <div className="flex-1 pb-6">
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 text-sm">
                      {getActivityLabel(activity.type)}
                    </span>
                    {activity.source === 'email' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Mail className="w-3 h-3 mr-1" />
                        Email
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                  </span>
                </div>

                {/* Campaign Badge for Email Activities */}
                {activity.metadata?.campaign_name && (
                  <div className="mb-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-purple-200">
                      📧 {activity.metadata.campaign_name}
                    </span>
                  </div>
                )}

                {/* Subject for Email Activities */}
                {activity.metadata?.subject && (
                  <div className="text-sm font-medium text-gray-700 mb-1">
                    {activity.metadata.subject}
                  </div>
                )}

                {/* Description/Snippet */}
                {activity.description && (
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {activity.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnifiedActivityTimeline;
